import { List, Spinner, SpinnerSize, Stack } from '@fluentui/react'
import * as React from 'react'
import { useContext, useEffect, useRef, useState } from 'react'

import { historyList, historyPin, historyRead } from '../../api'
import { Conversation } from '../../api/models'
import { AppStateContext } from '../../state/AppProvider'

import { GroupedChatHistory } from './ChatHistoryList'

import { PushPinOutlined } from '@mui/icons-material'
import { IconButton } from '@mui/material'
import { lowerCase, uniqBy } from 'lodash'
import { useLocation, useNavigate } from 'react-router-dom'
import { CustomTooltip } from '../CustomTooltip'
import styles from './ChatHistoryPanel.module.css'

interface ChatHistoryListItemCellProps {
  item?: Conversation
  onSelect: (item: Conversation | null) => void
}

interface ChatHistoryListItemGroupsProps {
  groupedChatHistory: GroupedChatHistory[]
}

const formatMonth = (month: string) => {
  const currentDate = new Date()
  const currentYear = currentDate.getFullYear()

  const [monthName, yearString] = month.split(' ')
  const year = parseInt(yearString)

  if (year === currentYear) {
    return monthName
  } else {
    return month
  }
}

export const ChatHistoryListItemCell: React.FC<ChatHistoryListItemCellProps> = ({ item, onSelect }) => {
  const [hoveredDivId, setHoveredDivId] = useState<string | null>(null)

  const appStateContext = React.useContext(AppStateContext)
  const isSelected = item?.id === appStateContext?.state.currentChat?.id
  const navigate = useNavigate()
  const location = useLocation()

  if (!item) {
    return null
  }

  const handlePinConversation = (convId: string) => {
    historyPin(convId, true)
      .then(response => {
        if (response && response?.ok) {
          const currentChatHistory = appStateContext?.state.chatHistory ?? []

          appStateContext?.dispatch({ type: 'UPDATE_CHAT_HISTORY', payload: { ...item, pinned: true } })
          appStateContext?.dispatch({
            type: 'FETCH_CHAT_HISTORY',
            payload: [{ ...item, pinned: true }, ...currentChatHistory.filter(conv => conv.id !== item.id)],
          })
          appStateContext?.dispatch({
            type: 'SET_SNACKBAR_STATE',
            payload: { message: 'Conversation pinned successfully.', severity: 'success' },
          })
        } else {
          appStateContext?.dispatch({
            type: 'SET_SNACKBAR_STATE',
            payload: { message: 'Pin failed. Try again later.', severity: 'warning' },
          })
        }
      })
      .catch(err => {
        appStateContext?.dispatch({
          type: 'SET_SNACKBAR_STATE',
          payload: { message: 'Pin failed. Try again later.', severity: 'warning' },
        })
      })
  }

  const handleUnpinConversation = (convId: string) => {
    historyPin(convId, false)
      .then(response => {
        if (response && response?.ok) {
          const currentChatHistory = appStateContext?.state.chatHistory ?? []
          appStateContext?.dispatch({ type: 'UPDATE_CHAT_HISTORY', payload: { ...item, pinned: false } })
          appStateContext?.dispatch({
            type: 'FETCH_CHAT_HISTORY',
            payload: [{ ...item, pinned: false }, ...currentChatHistory.filter(conv => conv.id !== item.id)],
          })
          appStateContext?.dispatch({
            type: 'SET_SNACKBAR_STATE',
            payload: { message: 'Conversation unpinned successfully.', severity: 'success' },
          })
        } else {
          appStateContext?.dispatch({
            type: 'SET_SNACKBAR_STATE',
            payload: { message: 'Unpin failed. Try again later.', severity: 'warning' },
          })
        }
      })
      .catch(err => {
        appStateContext?.dispatch({
          type: 'SET_SNACKBAR_STATE',
          payload: { message: 'Unpin failed. Try again later.', severity: 'warning' },
        })
      })
  }

  const handleSelectItem = (e: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>) => {
    if (lowerCase((e.target as HTMLInputElement).tagName) === 'div') {
      onSelect(item)

      historyRead(item.id)
        .then(conversation => {
          if (location.pathname !== '/app') {
            navigate('/app')
          }
          appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation })
          appStateContext?.dispatch({ type: 'SET_SHOULD_VALIDATE_FORM', payload: false })
        })
        .catch(err => {
          console.error(`Error fetching chat history for conversation ${item.id}`, err)
        })
    }
  }

  const truncatedTitle = item?.title?.length > 28 ? `${item.title.substring(0, 28)} ...` : item.title

  return (
    <Stack
      key={item.id}
      tabIndex={0}
      aria-label="chat history item"
      className={styles.itemCell}
      onClick={e => handleSelectItem(e)}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? handleSelectItem(e) : null)}
      onMouseEnter={() => setHoveredDivId(item.id)}
      onMouseLeave={() => setHoveredDivId(null)}
      verticalAlign="center"
      styles={{
        root: {
          backgroundColor: isSelected ? 'rgba(232, 208, 209, 1)' : 'transparent',
        },
      }}>
      <Stack
        horizontal
        verticalAlign={'center'}
        style={{ width: '100%', gap: '10px' }}>
        <div className={styles.chatTitle}>{truncatedTitle}</div>
        {(item.pinned || hoveredDivId === item.id) && (
          <CustomTooltip
            title={item.pinned ? 'Unpin' : 'Pin'}
            placement="right">
            <IconButton
              className={styles.pinIconButton}
              onClick={() => (item.pinned ? handleUnpinConversation(item.id) : handlePinConversation(item.id))}>
              <PushPinOutlined className={styles.pinIcon} />
            </IconButton>
          </CustomTooltip>
        )}
      </Stack>
    </Stack>
  )
}

export const ChatHistoryListItemGroups: React.FC<ChatHistoryListItemGroupsProps> = ({ groupedChatHistory }) => {
  const appStateContext = useContext(AppStateContext)
  const observerTarget = useRef(null)
  const [, setSelectedItem] = React.useState<Conversation | null>(null)
  const [offset, setOffset] = useState<number>(25)
  const [observerCounter, setObserverCounter] = useState(0)
  const [showSpinner, setShowSpinner] = useState(false)
  const firstRender = useRef(true)

  const handleSelectHistory = (item?: Conversation) => {
    if (item) {
      setSelectedItem(item)
    }
  }

  const onRenderCell = (item?: Conversation) => {
    return (
      <ChatHistoryListItemCell
        item={item}
        onSelect={() => handleSelectHistory(item)}
      />
    )
  }

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    handleFetchHistory()
    setOffset(offset => (offset += 25))
  }, [observerCounter])

  const handleFetchHistory = async () => {
    const currentChatHistory = appStateContext?.state.chatHistory
    setShowSpinner(true)

    await historyList(offset).then(response => {
      const concatenatedChatHistory = currentChatHistory && response && currentChatHistory.concat(...response)
      const uniqConcatenatedChatHistory = uniqBy(concatenatedChatHistory, 'id')
      if (response) {
        appStateContext?.dispatch({ type: 'FETCH_CHAT_HISTORY', payload: uniqConcatenatedChatHistory || response })
      } else {
        appStateContext?.dispatch({ type: 'FETCH_CHAT_HISTORY', payload: null })
      }
      setShowSpinner(false)
      return response
    })
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) setObserverCounter(observerCounter => (observerCounter += 1))
      },
      { threshold: 1 },
    )

    if (observerTarget.current) observer.observe(observerTarget.current)

    return () => {
      if (observerTarget.current) observer.unobserve(observerTarget.current)
    }
  }, [observerTarget])

  return (
    <div
      className={styles.listContainer}
      data-is-scrollable>
      {groupedChatHistory.map(
        group =>
          group.entries.length > 0 && (
            <Stack
              horizontalAlign="start"
              verticalAlign="center"
              key={group.timeFrame}
              className={styles.chatGroup}
              aria-label={`chat history group: ${group.timeFrame}`}>
              <Stack
                aria-label={group.timeFrame}
                className={styles.chatMonth}>
                {formatMonth(group.timeFrame)}
              </Stack>
              <List
                aria-label={`chat history list`}
                items={group.entries}
                onRenderCell={onRenderCell}
                className={styles.chatList}
              />
              <div
                ref={observerTarget}
                style={{ height: '0.5px' }}
              />
            </Stack>
          ),
      )}
      {showSpinner && (
        <div className={styles.spinnerContainer}>
          <Spinner
            size={SpinnerSize.small}
            aria-label="loading more chat history"
            className={styles.spinner}
          />
        </div>
      )}
    </div>
  )
}
